import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, datesBetween } from "@/lib/format";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const isoLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export function JobsCalendar({ jobs, onJobClick }) {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => new Date());
  const openJob = (j) => (onJobClick ? onJobClick(j) : navigate(`/jobs/${j.id}`));

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());

  const byDate = {};
  jobs.forEach((j) => {
    if (!j.preferred_date) return;
    const dates = datesBetween(
      addDays(j.preferred_date, -(j.flex_days_before || 0)),
      addDays(j.preferred_date, j.flex_days_after || 0)
    );
    dates.forEach((dt) => { (byDate[dt] = byDate[dt] || []).push(j); });
  });

  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }

  const todayIso = isoLocal(new Date());
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const undated = jobs.filter((j) => !j.preferred_date).length;

  const shiftMonth = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

  return (
    <div data-testid="jobs-calendar" className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <h3 className="font-display font-bold text-lg" data-testid="calendar-month-label">{monthLabel}</h3>
        <div className="flex items-center gap-1">
          <button data-testid="calendar-prev-btn" onClick={() => shiftMonth(-1)} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:border-kinetic transition-colors"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="calendar-today-btn" onClick={() => setCursor(new Date())} className="h-8 px-3 rounded-lg border border-border text-xs font-medium hover:border-kinetic transition-colors">Today</button>
          <button data-testid="calendar-next-btn" onClick={() => shiftMonth(1)} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:border-kinetic transition-colors"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center text-[11px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border">
        {WEEKDAYS.map((w) => <div key={w} className="py-2">{w}</div>)}
      </div>

      <div className="grid grid-cols-7">
        {days.map((d, idx) => {
          const key = isoLocal(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const dayJobs = byDate[key] || [];
          return (
            <div key={idx} data-testid={`calendar-day-${key}`}
              className={`min-h-[96px] border-b border-r border-border p-1.5 ${inMonth ? "" : "bg-muted/30"} ${idx % 7 === 0 ? "border-l" : ""}`}>
              <div className={`text-xs font-semibold mb-1 h-5 w-5 flex items-center justify-center rounded-full ${key === todayIso ? "bg-kinetic text-white" : inMonth ? "text-foreground" : "text-muted-foreground/50"}`}>
                {d.getDate()}
              </div>
              <div className="space-y-1">
                {inMonth && dayJobs.slice(0, 2).map((j) => (
                  <button key={j.id} data-testid={`calendar-job-${j.id}-${key}`} onClick={() => openJob(j)}
                    title={`${j.title} · ${j.bid_count || 0} bids`}
                    className={`w-full text-left truncate rounded px-1.5 py-1 text-[11px] font-medium border transition-colors ${j.my_bid ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100" : "bg-kinetic/10 border-kinetic/20 text-asphalt hover:bg-kinetic/20"}`}>
                    {j.title}
                  </button>
                ))}
                {inMonth && dayJobs.length > 2 && (
                  <button onClick={() => openJob(dayJobs[2])} className="w-full text-left text-[10px] text-muted-foreground hover:text-kinetic px-1.5">
                    +{dayJobs.length - 2} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {undated > 0 && (
        <p className="px-4 py-2 text-xs text-muted-foreground border-t border-border" data-testid="calendar-undated-note">
          {undated} open {undated === 1 ? "job has" : "jobs have"} no set date — find them in the list view.
        </p>
      )}
    </div>
  );
}
